import { useApolloClient } from '@apollo/client';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { extractListItems } from 'core/utils/apollo/extractListItems';
import { fetchAllPaginatedItems } from 'core/utils/apollo/fetchAllPaginatedItems';
import {
  GetUserCalendarOrganizationByOrganizationDocument,
  GetUserCalendarOrganizationByOrganizationQuery,
  GetUserCalendarOrganizationByOrganizationQueryVariables,
  UserCalendarStatus
} from 'generated-graphql/hooks/apollo-hooks';
import { useCallback, useEffect, useState } from 'react';

type Props = {
  search?: string;
  status?: UserCalendarStatus;
};

const PAGE_SIZE = 200;

type UserCalendarOrganizationItem = NonNullable<
  GetUserCalendarOrganizationByOrganizationQuery['getUserCalendarOrganizationByOrganization']
>['items'][number];

export const useUserCalendar = ({ search, status }: Props) => {
  const organization = useOrganization();
  const client = useApolloClient();
  const [items, setItems] = useState<
    NonNullable<UserCalendarOrganizationItem>[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const all = await fetchAllPaginatedItems<UserCalendarOrganizationItem>(
        async (nextToken) => {
          const { data } = await client.query<
            GetUserCalendarOrganizationByOrganizationQuery,
            GetUserCalendarOrganizationByOrganizationQueryVariables
          >({
            query: GetUserCalendarOrganizationByOrganizationDocument,
            variables: {
              organizationId: organization.id,
              limit: PAGE_SIZE,
              nextToken
            },
            fetchPolicy: 'no-cache'
          });
          return data?.getUserCalendarOrganizationByOrganization ?? null;
        }
      );
      setItems(all);
    } catch (err) {
      const wrapped = err instanceof Error ? err : new Error(String(err));
      console.error('Failed to load calendar contacts', wrapped);
      setItems([]);
      setError(wrapped);
    } finally {
      setLoading(false);
    }
  }, [client, organization.id]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const mapped = items.map((userCalendar) => {
    if (!userCalendar.userCalendar) {
      return null;
    }

    if (status && userCalendar.userCalendar.status !== status) {
      return null;
    }

    if (userCalendar.userCalendar.status === UserCalendarStatus.Invited) {
      return {
        ...userCalendar.userCalendar,
        userType: undefined,
        userCalendarOrganizationId: userCalendar.id
      };
    }

    return {
      ...userCalendar.userCalendar,
      userType: !userCalendar.userId ? 'External' : 'Internal',
      userCalendarOrganizationId: userCalendar.id
    };
  });

  let userCalendars = extractListItems(mapped);

  if (search) {
    userCalendars = userCalendars.filter((userCalendar) =>
      userCalendar.name.toLowerCase().includes(search.toLowerCase())
    );
  }

  return {
    loading,
    data: userCalendars,
    error,
    refetch
  };
};

export type UseUserCalendarReturnData = ReturnType<
  typeof useUserCalendar
>['data'];
export type UseUserCalendarReturnDataItem = UseUserCalendarReturnData[0];
